import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { blogAPI, authAPI } from '../../utils/api';
import { formatDate } from '../../utils/formatters';
import { Button } from '../ui/button';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '../ui/table';
import { BLOG_CATEGORIES } from '../../utils/constants';
import ImageUpload from './ImageUpload';
import ReactMarkdown from 'react-markdown';

interface BlogPost {
  _id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  author: string;
  categories: string[];
  tags: string[];
  published: boolean;
  createdAt: string;
  updatedAt: string;
  featuredImage?: string;
}

// Define the ref interface
interface AdminBlogPostsRef {
  editPostById: (id: string) => void;
}

const AdminBlogPosts = forwardRef<AdminBlogPostsRef, {}>((props, ref) => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [filter, setFilter] = useState<'all' | 'published' | 'draft'>('all');
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    categories: [BLOG_CATEGORIES[0]],
    tags: '',
    published: false,
    featuredImage: '',
  });

  const fetchPosts = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Make a single API call without specifying published status to get all posts
      const { data } = await blogAPI.getPosts(1, 100);
      
      // Extract blog posts from the response
      const blogPosts = data.blogPosts || data || [];
      
      // Process the posts to ensure published is a boolean
      const processedPosts = Array.isArray(blogPosts) ? blogPosts.map((post: any) => ({
        ...post,
        published: post.published === true
      })) : [];
      
      setPosts(processedPosts);
      setIsLoading(false);
    } catch (err: any) {
      console.error('Error fetching blog posts:', err);
      setError(err.response?.data?.message || 'Kunne ikke hente blog indlæg');
      setPosts([]); // Initialize with empty array to prevent undefined errors
      setIsLoading(false);
    }
  }, []);

  // Get filtered posts based on the current filter
  const filteredPosts = React.useMemo(() => {
    if (filter === 'published') return posts.filter(post => post.published === true);
    if (filter === 'draft') return posts.filter(post => post.published === false);
    return posts;
  }, [posts, filter]);

  useEffect(() => {
    // Check authentication status and then fetch posts
    const checkAuthAndFetchPosts = async () => {
      try {
        // We don't need to store the response if we're not using it
        await authAPI.checkAuth();
        
        // Always fetch posts, regardless of authentication status
        // The server will handle filtering based on the user's role
        fetchPosts();
      } catch (err) {
        console.error('Auth check failed:', err);
        // Still try to fetch posts even if auth check fails
        // The server will return only published posts for unauthenticated users
        fetchPosts();
      }
    };
    
    checkAuthAndFetchPosts();
  }, [fetchPosts]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked === true }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleImageUpload = (imageUrl: string) => {
    setFormData(prev => ({
      ...prev,
      featuredImage: imageUrl
    }));
  };

  const handleEditClick = (post: BlogPost) => {
    setSelectedPost(post);
    
    // Ensure published is a boolean
    const isPublished = post.published === true;
    
    setFormData({
      title: post.title,
      content: post.content,
      excerpt: post.excerpt,
      categories: post.categories || [BLOG_CATEGORIES[0]],
      tags: post.tags.join(', '),
      published: isPublished,
      featuredImage: post.featuredImage || '',
    });
    setIsEditing(true);
    setIsCreating(false);
    setPreviewMode(false);
  };

  // Method to find and edit a post by ID - exposed via ref
  const editPostById = (id: string) => {
    const postToEdit = posts.find(post => post._id === id);
    if (postToEdit) {
      handleEditClick(postToEdit);
    }
  };

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    editPostById
  }));

  const handleCreateClick = () => {
    setSelectedPost(null);
    setFormData({
      title: '',
      content: '',
      excerpt: '',
      categories: [BLOG_CATEGORIES[0]],
      tags: '',
      published: false,
      featuredImage: '',
    });
    setIsEditing(false);
    setIsCreating(true);
    setPreviewMode(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setIsCreating(false);
    setPreviewMode(false);
  };

  const togglePreviewMode = () => {
    setPreviewMode(!previewMode);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Ensure published is a boolean
      const postData = {
        ...formData,
        tags: formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag),
        published: formData.published === true
      };
      
      if (isEditing && selectedPost) {
        await blogAPI.updatePost(selectedPost._id, postData);
      } else if (isCreating) {
        await blogAPI.createPost(postData);
      }
      
      // Reset form and fetch updated posts
      setIsEditing(false);
      setIsCreating(false);
      setPreviewMode(false);
      await fetchPosts();
    } catch (err: any) {
      console.error('Error saving blog post:', err);
      alert(err.response?.data?.message || 'Der opstod en fejl ved gemning af blog indlæg');
    }
  };

  const handleDeletePost = async (id: string) => {
    if (!window.confirm('Er du sikker på, at du vil slette dette indlæg?')) {
      return;
    }
    
    try {
      await blogAPI.deletePost(id);
      
      // Refresh the posts list
      await fetchPosts();
      
      if (selectedPost?._id === id) {
        setSelectedPost(null);
        setIsEditing(false);
      }
    } catch (err) {
      console.error('Failed to delete blog post', err);
    }
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    
    if (checked) {
      // Add category
      setFormData(prev => ({
        ...prev,
        categories: [...prev.categories, value]
      }));
    } else {
      // Remove category
      setFormData(prev => ({
        ...prev,
        categories: prev.categories.filter(cat => cat !== value)
      }));
    }
  };

  if (isLoading && posts.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-snudefryd-blue"></div>
      </div>
    );
  }

  if (error && posts.length === 0) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  if (isEditing || isCreating) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-gray-800">
            {isEditing ? 'Rediger indlæg' : 'Opret nyt indlæg'}
          </h3>
          <div className="flex space-x-2">
            <Button variant="outline" onClick={togglePreviewMode}>
              {previewMode ? 'Rediger' : 'Forhåndsvis'}
            </Button>
            <Button variant="ghost" onClick={handleCancelEdit}>
              Annuller
            </Button>
          </div>
        </div>
        
        {previewMode ? (
          <div className="space-y-6">
            <div className="bg-gray-50 p-4 rounded-md">
              <h2 className="text-2xl font-bold mb-2">{formData.title}</h2>
              <div className="flex items-center space-x-2 mb-4">
                <span className="bg-snudefryd-pink text-white px-2 py-1 rounded text-xs">
                  {formData.categories.join(', ')}
                </span>
                <span className="text-gray-500 text-sm">
                  {new Date().toLocaleDateString('da-DK')}
                </span>
              </div>
              
              {formData.featuredImage && (
                <div className="mb-4">
                  <img 
                    src={formData.featuredImage} 
                    alt={formData.title} 
                    className="w-full h-64 object-cover rounded-md"
                  />
                </div>
              )}
              
              <div className="text-gray-600 italic mb-4">
                {formData.excerpt}
              </div>
              
              <div className="prose max-w-none">
                <ReactMarkdown>{formData.content}</ReactMarkdown>
              </div>
              
              <div className="mt-4 flex flex-wrap gap-2">
                {formData.tags.split(',').map((tag, index) => (
                  tag.trim() && (
                    <span 
                      key={index} 
                      className="bg-gray-200 text-gray-700 px-2 py-1 rounded text-xs"
                    >
                      {tag.trim()}
                    </span>
                  )
                ))}
              </div>
            </div>
            
            <Button onClick={() => setPreviewMode(false)} className="w-full">
              Tilbage til redigering
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                Titel
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-snudefryd-blue focus:border-snudefryd-blue"
              />
            </div>
            
            <div>
              <label htmlFor="excerpt" className="block text-sm font-medium text-gray-700 mb-1">
                Uddrag
              </label>
              <textarea
                id="excerpt"
                name="excerpt"
                value={formData.excerpt}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-snudefryd-blue focus:border-snudefryd-blue"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fremhævet billede
              </label>
              <ImageUpload 
                onImageUpload={handleImageUpload} 
                currentImage={formData.featuredImage}
              />
            </div>
            
            <div>
              <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">
                Indhold (Markdown understøttet)
              </label>
              <textarea
                id="content"
                name="content"
                value={formData.content}
                onChange={handleInputChange}
                required
                rows={10}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-snudefryd-blue focus:border-snudefryd-blue font-mono"
              />
              <p className="text-xs text-gray-500 mt-1">
                Brug Markdown til formatering. F.eks. # for overskrifter, ** for fed tekst, * for kursiv, ![billedtekst](https://url-til-billede.dk) for at indsætte billeder fra links.
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kategorier
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {BLOG_CATEGORIES.map((category) => (
                  <div key={category} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`category-${category}`}
                      value={category}
                      checked={formData.categories.includes(category)}
                      onChange={handleCategoryChange}
                      className="h-4 w-4 text-snudefryd-blue focus:ring-snudefryd-blue border-gray-300 rounded"
                    />
                    <label htmlFor={`category-${category}`} className="ml-2 text-sm text-gray-700">
                      {category}
                    </label>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <label htmlFor="tags" className="block text-sm font-medium text-gray-700 mb-1">
                Tags (kommasepareret)
              </label>
              <input
                type="text"
                id="tags"
                name="tags"
                value={formData.tags}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-snudefryd-blue focus:border-snudefryd-blue"
              />
            </div>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                id="published"
                name="published"
                checked={formData.published}
                onChange={handleInputChange}
                className="h-4 w-4 text-snudefryd-blue focus:ring-snudefryd-blue border-gray-300 rounded"
              />
              <label htmlFor="published" className="ml-2 text-sm text-gray-700">
                Publiceret
              </label>
              {!formData.published && (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  Kladde
                </span>
              )}
            </div>
            
            <div className="pt-4">
              <Button type="submit" className="w-full">
                {isEditing ? 'Gem ændringer' : 'Opret indlæg'}
              </Button>
            </div>
          </form>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <h2 className="text-2xl font-bold text-gray-800">Blog Indlæg</h2>
        <div className="flex flex-wrap gap-2">
          <div className="flex rounded-md shadow-sm" role="group">
            <button
              type="button"
              onClick={() => setFilter('all')}
              className={`px-3 py-1.5 text-xs font-medium rounded-l-lg ${
                filter === 'all' 
                  ? 'bg-snudefryd-blue text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Alle ({posts.length})
            </button>
            <button
              type="button"
              onClick={() => setFilter('published')}
              className={`px-3 py-1.5 text-xs font-medium ${
                filter === 'published' 
                  ? 'bg-snudefryd-blue text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Publiceret ({posts.filter(post => post.published).length})
            </button>
            <button
              type="button"
              onClick={() => setFilter('draft')}
              className={`px-3 py-1.5 text-xs font-medium rounded-r-lg ${
                filter === 'draft' 
                  ? 'bg-snudefryd-blue text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Kladder ({posts.filter(post => !post.published).length})
            </button>
          </div>
          <Button onClick={handleCreateClick}>Opret nyt indlæg</Button>
        </div>
      </div>

      {filteredPosts.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
          <p className="text-gray-600">
            {filter === 'all' 
              ? 'Ingen blog indlæg fundet.' 
              : filter === 'published' 
                ? 'Ingen publicerede indlæg fundet.' 
                : 'Ingen kladder fundet.'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {/* Mobile view - card layout */}
          <div className="md:hidden space-y-4 p-4">
            {filteredPosts.map((post) => (
              <div key={post._id} className="border rounded-lg p-4 space-y-3">
                <div className="font-medium text-lg">{post.title}</div>
                <div className="text-sm text-gray-500">
                  {formatDate(post.createdAt)}
                  {!post.published && (
                    <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      Kladde
                    </span>
                  )}
                </div>
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Kategori:</span> {post.categories.join(', ')}
                </div>
                <div className="flex justify-end space-x-2">
                  <Button variant="outline" size="sm" onClick={() => handleEditClick(post)}>
                    Rediger
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => handleDeletePost(post._id)}>
                    Slet
                  </Button>
                </div>
              </div>
            ))}
          </div>
          
          {/* Desktop view - table layout */}
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Titel</TableHead>
                  <TableHead>Kategori</TableHead>
                  <TableHead>Dato</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Handlinger</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPosts.map((post) => (
                  <TableRow key={post._id}>
                    <TableCell className="font-medium">{post.title}</TableCell>
                    <TableCell>{post.categories.join(', ')}</TableCell>
                    <TableCell>{formatDate(post.createdAt)}</TableCell>
                    <TableCell>
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${
                          post.published
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {post.published ? 'Publiceret' : 'Kladde'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditClick(post)}
                        >
                          Rediger
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-800 hover:bg-red-50"
                          onClick={() => handleDeletePost(post._id)}
                        >
                          Slet
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
});

export default AdminBlogPosts; 